import React, { useState, useEffect } from 'react';
import PWAPrompt from 'react-ios-pwa-prompt';

// Clé pour stocker l'information d'affichage dans localStorage
const PWA_PROMPT_SHOWN_KEY = 'pwa_prompt_shown_count';
const PWA_PROMPT_LAST_SHOWN_KEY = 'pwa_prompt_last_shown';
const PWA_PROMPT_DISMISSED_KEY = 'pwa_prompt_dismissed';

// Singleton pour éviter les doubles notifications
let promptInstanceExists = false;

const IOSInstallPrompt: React.FC = () => {
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    // Si une instance existe déjà, ne pas afficher cette instance
    if (promptInstanceExists) {
      return;
    }
    
    // Marquer cette instance comme existante
    promptInstanceExists = true;
    
    // Nettoyer lors du démontage
    return () => {
      promptInstanceExists = false;
    };
  }, []);

  useEffect(() => {
    // Vérifier si nous devons afficher le prompt
    const checkIfShouldShowPrompt = () => {
      // Vérifier si la notice a été définitivement rejetée
      if (localStorage.getItem(PWA_PROMPT_DISMISSED_KEY) === 'true') {
        return false;
      }
      
      // Vérifier si nous sommes sur une plateforme Apple
      const isAppleDevice = /iPad|iPhone|iPod|Macintosh/.test(navigator.userAgent) && 
                           (/Safari/.test(navigator.userAgent) || /AppleWebKit/.test(navigator.userAgent));
      
      if (!isAppleDevice) {
        return false;
      }
      
      // Vérifier combien de fois le prompt a été affiché
      const shownCount = parseInt(localStorage.getItem(PWA_PROMPT_SHOWN_KEY) || '0', 10);
      
      // Si déjà affiché 3 fois, ne plus afficher
      if (shownCount >= 3) {
        // Marquer comme définitivement rejeté après 3 tentatives
        localStorage.setItem(PWA_PROMPT_DISMISSED_KEY, 'true');
        return false;
      }
      
      // Vérifier quand le prompt a été affiché pour la dernière fois
      const lastShown = parseInt(localStorage.getItem(PWA_PROMPT_LAST_SHOWN_KEY) || '0', 10);
      const now = Date.now();
      
      // Ne pas afficher plus d'une fois par jour (86400000 ms = 1 jour)
      if (lastShown > 0 && now - lastShown < 86400000) {
        return false;
      }
      
      return true;
    };
    
    // Définir si nous devons afficher le prompt avec un léger délai
    // pour éviter de perturber le chargement initial
    const timeoutId = setTimeout(() => {
      setShowPrompt(checkIfShouldShowPrompt());
    }, 2000);
    
    return () => clearTimeout(timeoutId);
  }, []);
  
  // Fonction appelée lorsque le prompt est fermé
  const handlePromptShown = () => {
    // Incrémenter le compteur
    const currentCount = parseInt(localStorage.getItem(PWA_PROMPT_SHOWN_KEY) || '0', 10);
    localStorage.setItem(PWA_PROMPT_SHOWN_KEY, (currentCount + 1).toString());
    
    // Enregistrer la date actuelle
    localStorage.setItem(PWA_PROMPT_LAST_SHOWN_KEY, Date.now().toString());
    
    // Si fermé 3 fois, marquer comme définitivement rejeté
    if (currentCount + 1 >= 3) {
      localStorage.setItem(PWA_PROMPT_DISMISSED_KEY, 'true');
    }
    
    setShowPrompt(false);
  };

  // Si nous ne devons pas afficher le prompt, ne rien rendre
  if (!showPrompt) {
    return null;
  }

  return (
    <PWAPrompt 
      timesToShow={1}
      delay={100} 
      copyTitle="Installer TCG Pocket"
      copyDescription="Ajoutez TCG Pocket à votre écran d'accueil pour y accéder plus facilement."
      copyShareStep="Appuyez sur le bouton 'Partager'"
      copyAddToHomeScreenStep="Appuyez sur 'Sur l'écran d'accueil'"
      appIconPath="/icons/icon-192x192.png"
      onClose={handlePromptShown}
    />
  );
};

export default IOSInstallPrompt; 